import { useNavigate } from 'react-router-dom';
import { Stack, Group, Text, Badge, Button } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TenantName } from '../../../../components/TenantName';
import { CopyableId } from '../../../../components/CopyableId';
import { formatDateTime } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';
import { OUTCOME_COLOR, OUTCOME_LABEL_KEY, outcomeOf, type Delivery } from './deliveryState';
import '../../webhooks.css';

/**
 * One delivery attempt, read whole.
 *
 * The table has to keep every row on one line, so the values a person actually
 * quotes when they open a ticket (the delivery id, the result id, the full
 * blocked reason, the endpoint the attempt was aimed at) have nowhere to live
 * there. They live here.
 *
 * There is no Response section and no Replay button because neither exists to
 * show: the admin projection carries no response body, and replay and delete
 * are tenant-scoped routes that this console cannot call. Both absences are
 * stated rather than left as a gap the operator has to infer.
 */
export function DeliveryDrawer({ row, endpointUrl, onClose }: {
  row: Delivery | null;
  /** Resolved from the subscriptions cache, because the delivery row has only an id. */
  endpointUrl?: string;
  onClose: () => void;
}) {
  const t = useT();
  const nav = useNavigate();
  const outcome = row ? outcomeOf(row) : null;

  return (
    <Drawer opened={row !== null} onClose={onClose} title={t('webhooks.deliveries.detail.title')} position="right" size="md">
      {row && outcome && (
        <Stack gap="lg">
          <Group gap="sm" align="center" wrap="wrap">
            <Badge size="sm" variant="light" color={OUTCOME_COLOR[outcome]}>{t(OUTCOME_LABEL_KEY[outcome])}</Badge>
            {row.http_status != null && (
              <Text role="bodySmall" fw={600}>{t('webhooks.deliveries.columns.http')} {row.http_status}</Text>
            )}
            <CopyableId value={row.id} label={t('webhooks.deliveries.detail.deliveryId')} />
          </Group>

          {outcome === 'blocked' && row.blocked_reason && (
            <Alert color="warning">{t('webhooks.deliveries.blockedReason', { reason: row.blocked_reason })}</Alert>
          )}

          <Stack gap="sm" className="rr-wh-detail__section">
            <Text role="overline" c="var(--rr-color-text-muted)">{t('webhooks.deliveries.detail.overview')}</Text>
            <div className="rr-wh-detail__grid">
              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.deliveries.detail.tenant')}</Text>
              <TenantName id={row.organisation_id} />

              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.deliveries.detail.endpoint')}</Text>
              <Text role="bodySmall" className="rr-wh-detail__url">
                {endpointUrl ?? t('webhooks.deliveries.unknownEndpoint')}
              </Text>

              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.deliveries.detail.eventType')}</Text>
              <Text role="bodySmall"><code>{row.event_type}</code></Text>

              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.deliveries.detail.attempt')}</Text>
              <Text role="bodySmall">{row.attempt_number}</Text>

              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.deliveries.detail.scheduledAt')}</Text>
              <Text role="bodySmall">{formatDateTime(row.scheduled_at)}</Text>

              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.deliveries.detail.attemptedAt')}</Text>
              <Text role="bodySmall">
                {row.attempted_at ? formatDateTime(row.attempted_at) : t('webhooks.deliveries.neverAttempted')}
              </Text>

              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.deliveries.detail.latency')}</Text>
              <Text role="bodySmall">
                {row.latency_ms == null
                  ? t('common.notAvailable')
                  : t('webhooks.deliveries.latencyMs', { value: row.latency_ms })}
              </Text>
            </div>
          </Stack>

          <Stack gap="sm" className="rr-wh-detail__section">
            <Text role="overline" c="var(--rr-color-text-muted)">{t('webhooks.deliveries.detail.identifiers')}</Text>
            <div className="rr-wh-detail__grid">
              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.deliveries.detail.deliveryId')}</Text>
              <CopyableId value={row.id} chars={14} label={t('webhooks.deliveries.detail.deliveryId')} />

              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.deliveries.detail.resultId')}</Text>
              <CopyableId value={row.result_id} chars={14} label={t('webhooks.deliveries.detail.resultId')} />

              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.deliveries.detail.subscription')}</Text>
              <CopyableId value={row.subscription_id} chars={14} label={t('webhooks.deliveries.copySubscriptionId')} />
            </div>
          </Stack>

          <Stack gap="sm">
            <Group gap="sm" wrap="wrap">
              <Button size="sm" variant="default" onClick={() => nav(`/webhooks?subscription=${row.subscription_id}`)}>
                {t('webhooks.deliveries.viewSubscription')}
              </Button>
            </Group>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.deliveries.detail.noResponseBody')}</Text>
            <Alert color="info">{t('webhooks.deliveries.detail.readOnly')}</Alert>
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
}
